import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import config from "../../config";
import { store } from "../../Store";

const env = process.env.HOST;

let sentryEnv = "";
if (env.includes("cv")) {
    if (env.includes("qa")) sentryEnv = "cv-qa";
    else if (env.includes("prod")) sentryEnv = "cv-production";
} else if (env.includes("uv")) {
    if (env.includes("staging")) sentryEnv = "uv-staging";
    else if (env.includes("prod")) sentryEnv = "uv-production";
}

//  Initialize Sentry
Sentry.init({
    dsn: config.sentryDsn,
    integrations: [new BrowserTracing()],
    // transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    transportOptions: {
        dbName: "cv-sentry-logs",
        maxQueueSize: 30
    },
    tracesSampleRate: 1.0, // Adjust for production
    environment: sentryEnv,
    enabled:
        env.includes("prod") || env.includes("qa") || env.includes("staging")
});

// Capture unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
    logToSentry("Unhandled Promise Rejection", { error: event.reason });
});

// Capture console errors
window.addEventListener("error", (event) => {
    logToSentry("Console Error", { error: event.error });
});

window.onerror = (message, source, lineno, colno, error) => {
    if (error) {
        logToSentry(`Global excpection`, {});
    }
};

/**
 * Update Sentry User Context
 * This ensures that the latest user data is attached after login
 */
export const updateSentryUserContext = () => {
    try {
        const { auth } = store.getState();

        const authDetails = {
            id: auth.account.id,
            email: auth.account.email,
            username: auth.username,
            language: auth.account.language
        };
        // console.log("authDetails", authDetails);
        if (auth.username.length > 0 && auth.idToken.length > 0) {
            Sentry.setUser(authDetails);
        } else {
            Sentry.setUser(null); // Clear user context if logged out
        }
    } catch (error) {
        console.warn("Failed to update Sentry user context", error);
        Sentry.setUser(null);
    }
};

/**
 * Recover a real exception to hand Sentry from whatever a call site passed.
 * Call sites pass a caught Error under `context.error` most of the time, but
 * also: a plain `{ name, message, errorCode }` snapshot, an already-stringified
 * message, or the error itself passed directly as `context` (the sibling
 * auditor-pwa-app repo has a call site shaped that way). Whatever shape it
 * is, build a real Error so Sentry.captureException gets something with a
 * useful message/stack instead of the literal log label every time.
 */
const toException = (message, context) => {
    let candidate;
    if (context && typeof context === "object" && "error" in context) {
        candidate = context.error;
    } else if (context instanceof Error) {
        candidate = context;
    } else if (context && typeof context === "object" && typeof context.message === "string") {
        // Guards against a call site that passes an error-shaped object
        // directly as `context` instead of wrapping it in `{ error }`.
        candidate = context;
    }

    if (candidate instanceof Error) return candidate;

    if (candidate && typeof candidate === "object" && typeof candidate.message === "string") {
        const err = new Error(`${message}: ${candidate.message}`);
        if (candidate.name) err.name = candidate.name;
        return err;
    }

    if (typeof candidate === "string" && candidate) {
        return new Error(`${message}: ${candidate}`);
    }

    return new Error(message);
};

/**
 * Reduce a log label to something safe to index as a Sentry tag: first clause
 * only, trimmed, length-capped. "on loading report: TypeError: x is not a
 * function" becomes "on loading report".
 */
const LOG_CONTEXT_TAG_MAX = 80;

const logContextTag = (message) => {
    if (typeof message !== "string") return "unknown";
    const label = message.split(":")[0].trim() || message.trim();
    return label.slice(0, LOG_CONTEXT_TAG_MAX) || "unknown";
};

export const logToSentry = (message, context = {}) => {
    const errorDetails = {
        errorMessage: message,
        controllerVersion: process.env.version,
        ...context
    };
    Sentry.addBreadcrumb({
        category: "error",
        message: message,
        level: "error",
        data: errorDetails
    });
    console.error("Sentry Error Log:", errorDetails);
    // The tag is indexed by Sentry, so it has to be a low-cardinality label.
    // Some call sites interpolate the error text into `message`, which would
    // mint a new tag value per distinct error — the variable part goes in
    // `extra` (already carried by errorDetails) and the tag gets the stable
    // prefix only.
    Sentry.captureException(toException(message, context), {
        extra: errorDetails,
        tags: { logContext: logContextTag(message) }
    });
};

export default Sentry;
